<template>
  <div class="contact-info">
    <form v-if="userRef">
      <div class="row input-row">
        <div class="col-8">
          <label for="givenNameInput" class="form-label">Given name</label>
          <input type="text" class="form-control" id="givenNameInput" :readonly="disabled" v-model="editUser.givenName"/>
        </div>
        <div class="col-8">
          <label for="familyNameInput" class="form-label">Surname</label>
          <input type="text" class="form-control" id="familyNameInput" :readonly="disabled" v-model="editUser.surname"/>
        </div>
        <div class="col-4">
          <PrivacyLevelSelect id="namePrivacySelect" :disabled="disabled" v-model="editUser.namePrivacy"/>
        </div>
      </div>
      <div class="row input-row">
        <div class="col-3">
          <label for="genderSelect" class="form-label">Gender</label>
          <select class="form-select" id="genderSelect" :disabled="disabled" v-model="editUser.gender">
            <option></option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>
      <div class="row input-row">
        <div class="col-8">
          <label for="emailInput" class="form-label">Contact email</label>
          <input type="text" class="form-control" id="emailInput" :readonly="disabled" v-model="editUser.email"/>
        </div>
        <div class="col-4">
          <PrivacyLevelSelect id="emailPrivacySelect" :disabled="disabled" v-model="editUser.emailPrivacy"/>
        </div>
      </div>
      <div class="row input-row">
        <div class="col-8">
          <label for="altEmailInput" class="form-label">Alternate email</label>
          <input type="text" class="form-control" id="altEmailInput" :readonly="disabled" v-model="editUser.email2"/>
        </div>
        <div class="col-4">
          <PrivacyLevelSelect id="email2PrivacySelect" :disabled="disabled" v-model="editUser.email2Privacy"/>
        </div>
      </div>
      <div class="row input-row">
        <div class="col-8">
          <label for="mobileNumberInput" class="form-label">Mobile number</label>
          <input type="text" class="form-control" id="mobileNumberInput" :readonly="disabled" v-model="editUser.mobileNumber"/>
        </div>
        <div class="col-4">
          <PrivacyLevelSelect id="mobileNumberPrivacySelect" :disabled="disabled" v-model="editUser.mobileNumberPrivacy"/>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.row.input-row.private {
  margin-bottom: 0;
  padding-bottom: 1rem;
  background-color: #f8f8f8;
}
</style>

<script setup lang="ts">
import { reactive, computed, watch } from "vue"
import { useUserStore } from '@/gp/UserStore'
import { User } from '@/gp/GroupAdminModel'
import { PatchChange, PatchChangeValue } from '@/util/Api'
import { useTokenManager } from "@/auth/TokenManager"
import PrivacyLevelSelect from '@/account/PrivacyLevelSelect.vue'
import { propertyChanged } from "@/util/EditMode"

const props = defineProps<{
  userId?: string,
  disabled?: boolean
}>()

defineExpose({
  hasChanges,
  canSave,
  saveAsync
})

const userStore = useUserStore()
const tokenManager = useTokenManager()

const userIdRef = computed(() => props.userId ?? tokenManager.current.userId!)
const userRef = computed(() => userStore.getAsyncUser(userIdRef.value)?.data)
const editUser = reactive(new User())
const editProperties = [
  'givenName',
  'surname', 
  'namePrivacy',
  'gender',
  'email',
  'emailPrivacy',
  'email2',
  'email2Privacy',
  'mobileNumber',
  'mobileNumberPrivacy',
] as (keyof User)[]

watch(() => props.userId, () => {
  if (userRef.value) {
    Object.assign(editUser, userRef.value)
  }
}, { immediate: true })

function hasChanges() {
  return userRef.value && editProperties.some(key => propertyChanged(editUser[key], userRef.value![key]))
}

function canSave() {
  return hasChanges() && (!!editUser.givenName || !!editUser.surname)
}

async function saveAsync() {
  if (!userRef.value)
    return
  
  const changes: PatchChange[] = []
  for (const key of editProperties) {
    if (editUser[key] != userRef.value[key]) {
      changes.push(new PatchChange(`/${key}`, editUser[key] as PatchChangeValue))
    }
  }

  if (changes.length) {
    await userStore.patchAsync(userIdRef.value!, changes)
  }
}
</script>
